import React, {memo, useCallback, useRef, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {onClickHandler} from '../../../../utils/onclick';
import {useRPC} from '../../../../hooks/rpc';
import {useMessage} from '../../../../hooks/message';
import {downloadStringAsFile} from '../../../../utils/download';

import './index.scss';
import {useQuestion} from '../../../../hooks/question';
import {useTranslation} from "react-i18next";
import {useHistory} from 'react-router-dom';

export const DatabaseScreen = memo(function TestScreen() {
    const {t} = useTranslation();
    const [rpc] = useRPC();
    const {success, failure} = useMessage();
    const ask = useQuestion();
    const history = useHistory();

    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileNameToUpload, setFileNameToUpload] = useState('');

    const downloadInputRef = useRef();

    const downloadDBClick = useCallback(() => {
        if (downloading) {
            return;
        }

        setDownloading(true);
        rpc.downloadDB()
            .then(response => {
                downloadStringAsFile(response, 'db.zip');
            })
            .catch(failure)
            .finally(() => setDownloading(false));
    }, [rpc, failure, downloading]);

    const resetDBClick = useCallback(() => {
        ask(t('database_will_be_reset_to_factory_settings__are_you_sure'), () => {
            rpc.resetDB()
                .then(() => {
                    success('');
                })
                .catch(failure);
        });
    }, [t, rpc, success, failure, ask]);

    const uploadDBClick = useCallback(() => {
        if (uploading) {
            return;
        }

        // @ts-ignore
        downloadInputRef?.current?.click();
    }, [uploading]);

    const uploadDBFile = useCallback((file: File) => {
        setUploading(true);
        const fileReader = new FileReader();
        // @ts-ignore
        fileReader.onload = () => {
            rpc.uploadDB(fileReader.result)
                .catch(failure)
                .finally(() => setUploading(false));
        };
        fileReader.readAsDataURL(file);
    }, [rpc, failure]);

    const onInputFileChange = useCallback(event => {
        const target = event.target;
        const file = target.files[0];
        setFileNameToUpload(file.name);
        uploadDBFile(file);
        event.target.value = null;
    }, [uploadDBFile]);

    const logsPage = () => history.push('/settings/db/logs');

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={[]}
                backUrl="/settings">
                <div className="content-divider">
                    <div className="content-wrapper">
                        <div className="database-screen-container">
                            <div className="button" {...onClickHandler(downloadDBClick)}>
                                <span className="text">{downloading ? t('downloading') : t('download_database')}</span>
                            </div>
                            <div className="button" {...onClickHandler(uploadDBClick)}>
                                <span className="text">{uploading ? t('filename_uploading', {fileName: fileNameToUpload}) : t('upload_database')}</span>
                                <input
                                    type="file"
                                    ref={downloadInputRef}
                                    style={{display: 'none'}}
                                    onChange={onInputFileChange}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="content-wrapper">
                        <div className="database-screen-container">
                            <div className="button" {...onClickHandler(resetDBClick)}>
                                <span className="text">{t('reset_database')}</span>
                            </div>
                        </div>
                    </div>
                    <div className="content-wrapper">
                        <div className="database-screen-container">
                            <div className="button" {...onClickHandler(logsPage)}>
                                <span className="text">{t('download_log')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
